import React, { useState, useEffect, useMemo } from 'react';
import {
    Checkbox,
    LinearProgress,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TablePagination,
    TableRow,
} from '@mui/material';
import {
    PictureAsPdf as PdfIcon,
    TableChart as ExcelIcon,
    Visibility as ViewIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import AssignmentService from '../../../Services/assignment.service';
import UserService from '../../../Services/user.service';
import Constants from '../../../assests/Constants/constants';
import UserAvatar from '../../Widgets/UserAvatar/UserAvatar.js';
import CustomDatePicker from '../../Widgets/CustomDates/CustomDatePicker/CustomDatePicker';
import XtramModal, {
    XtramBadge,
    XtramButton,
    XtramCheckbox,
    XtramField,
    XtramSelect,
    XtramStat,
    XtramTable,
    xtramStyles as styles,
} from '../../Widgets/XtramModal/XtramModal';
import { priorityEmoji, priorityKey, priorityTone, statusKey, statusTone } from './assignmentDisplay';

const ALL = 'all';

const WorkPlanManager = ({ open, onClose, assignments, subAccountants, caseIdOptions, categoryOptions }) => {
    const { t, i18n } = useTranslation();
    const direction = i18n.dir();
    const [loading, setLoading] = useState(false);
    const [selectedAssignments, setSelectedAssignments] = useState([]);
    const [workPlanRequest, setWorkPlanRequest] = useState({
        startDate: dayjs().startOf('week').toDate(),
        endDate: dayjs().endOf('week').toDate(),
        assignmentIds: [],
        caseId: null,
        assigneeId: null,
        status: null
    });
    const [generatedReport, setGeneratedReport] = useState(null);
    const [showReport, setShowReport] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const [user, setUser] = useState(null);
    const [token, setToken] = useState('');

    useEffect(() => {
        const fetchUserData = async () => {
            const currentUser = await UserService.getCurrentUser();
            const currentToken = await UserService.getCurrentToken();
            setUser(currentUser);
            setToken(currentToken);
        };
        fetchUserData();
    }, []);

    const clientOptions = useMemo(() => [
        { value: ALL, label: t('AllClients') },
        ...caseIdOptions.filter((option) => option.value),
    ], [caseIdOptions, t]);

    const assigneeOptions = useMemo(() => [
        { value: ALL, label: t('AllAssignees') },
        ...subAccountants.map((assignee) => ({ value: assignee.subAccountantId, label: assignee.name })),
    ], [subAccountants, t]);

    const statusOptions = useMemo(() => [
        { value: ALL, label: t('AllStatuses') },
        { value: Constants.StatusEnum.ToDo, label: t('ToDo') },
        { value: Constants.StatusEnum.InProgress, label: t('InProgress') },
        { value: Constants.StatusEnum.Done, label: t('Done') },
    ], [t]);

    const pickFilter = (field) => (option) => setWorkPlanRequest((prev) => ({
        ...prev,
        [field]: !option || option.value === ALL ? null : option.value,
    }));

    const selectedOption = (options, value) => options.find((o) => o.value === (value ?? ALL)) || options[0];

    const handleAssignmentSelection = (assignmentId, checked) => {
        if (checked) {
            setSelectedAssignments(prev => [...prev, assignmentId]);
        } else {
            setSelectedAssignments(prev => prev.filter(id => id !== assignmentId));
        }
    };

    const handleSelectAllAssignments = (checked) => {
        if (checked) {
            setSelectedAssignments(filteredAssignments.map(a => a.id));
        } else {
            setSelectedAssignments([]);
        }
    };

    const filteredAssignments = useMemo(() => {
        let filtered = assignments;

        if (workPlanRequest.caseId) {
            filtered = filtered.filter(a => a.caseId === workPlanRequest.caseId);
        }

        if (workPlanRequest.assigneeId) {
            filtered = filtered.filter(a => a.assigneeId === workPlanRequest.assigneeId);
        }

        if (workPlanRequest.status !== null) {
            filtered = filtered.filter(a => a.status === workPlanRequest.status);
        }

        return filtered;
    }, [assignments, workPlanRequest]);

    const buildRequest = () => ({
        accountantId: user.accountantId,
        startDate: workPlanRequest.startDate,
        endDate: workPlanRequest.endDate,
        assignmentIds: selectedAssignments.length > 0 ? selectedAssignments : [],
        // Only send filters if no specific assignments are selected
        caseId: selectedAssignments.length === 0 ? (workPlanRequest.caseId || null) : null,
        assigneeId: selectedAssignments.length === 0 ? (workPlanRequest.assigneeId || null) : null,
        status: selectedAssignments.length === 0 ? workPlanRequest.status : null
    });

    const handleGenerateReport = async () => {
        if (!user?.accountantId) {
            toast.error(t('UserNotAuthenticated'));
            return;
        }

        // Validate that either assignments are selected or a period is specified
        if (selectedAssignments.length === 0 && (!workPlanRequest.startDate || !workPlanRequest.endDate)) {
            toast.error(t('PleaseSelectAssignmentsOrPeriod'));
            return;
        }

        setLoading(true);
        try {
            const response = await AssignmentService.GenerateWorkPlanReport(buildRequest(), token);
            if (response.ok) {
                const report = await response.json();
                setGeneratedReport(report);
                setShowReport(true);
                toast.success(t('ReportGeneratedSuccessfully'));
            } else {
                toast.error(t('FailedToGenerateReport'));
            }
        } catch (error) {
            console.error('Error generating report:', error);
            toast.error(t('ErrorGeneratingReport'));
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadPdf = async () => {
        if (!user?.accountantId) return;

        setLoading(true);
        try {
            await AssignmentService.GenerateWorkPlanPdf(buildRequest(), token);
            toast.success(t('PdfDownloadedSuccessfully'));
        } catch (error) {
            console.error('Error downloading PDF:', error);
            toast.error(t('ErrorDownloadingPdf'));
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadExcel = async () => {
        if (!user?.accountantId) return;

        setLoading(true);
        try {
            await AssignmentService.GenerateWorkPlanExcel(buildRequest(), token);
            toast.success(t('ExcelDownloadedSuccessfully'));
        } catch (error) {
            console.error('Error downloading Excel:', error);
            toast.error(t('ErrorDownloadingExcel'));
        } finally {
            setLoading(false);
        }
    };

    const getAssigneeName = (assigneeId) => {
        const assignee = subAccountants.find(sa => sa.subAccountantId === assigneeId);
        return assignee ? assignee.name : t('NoAssignee');
    };

    const getClientName = (caseId) => {
        const client = caseIdOptions.find(c => c.value === caseId);
        return client ? client.label : '-';
    };

    const renderStatus = (status) => (
        <XtramBadge tone={statusTone(status)}>{t(statusKey(status) || '')}</XtramBadge>
    );

    const renderPriority = (priority) => (
        <XtramBadge tone={priorityTone(priority)}>
            {priorityEmoji(priority)} {t(priorityKey(priority) || '')}
        </XtramBadge>
    );

    const allSelected = selectedAssignments.length === filteredAssignments.length && filteredAssignments.length > 0;
    const someSelected = selectedAssignments.length > 0 && !allSelected;

    const renderConfiguration = () => (
        <>
            <div className={styles.section}>
                <h3 className={styles.sectionTitle}>{t('ReportConfiguration')}</h3>
                <div className={styles.formFields}>
                    <XtramField label={t('StartDate')}>
                        <CustomDatePicker
                            date={workPlanRequest.startDate ? dayjs(workPlanRequest.startDate) : null}
                            onDateChange={(date) => date && setWorkPlanRequest(prev => ({ ...prev, startDate: date }))}
                            height="40px"
                        />
                    </XtramField>
                    <XtramField label={t('EndDate')}>
                        <CustomDatePicker
                            date={workPlanRequest.endDate ? dayjs(workPlanRequest.endDate) : null}
                            onDateChange={(date) => date && setWorkPlanRequest(prev => ({ ...prev, endDate: date }))}
                            height="40px"
                        />
                    </XtramField>
                    <XtramField label={t('Client')}>
                        <XtramSelect
                            direction={direction}
                            options={clientOptions}
                            value={selectedOption(clientOptions, workPlanRequest.caseId)}
                            onChange={pickFilter('caseId')}
                            placeholder={t('SelectClient')}
                        />
                    </XtramField>
                    <XtramField label={t('Assignee')}>
                        <XtramSelect
                            direction={direction}
                            options={assigneeOptions}
                            value={selectedOption(assigneeOptions, workPlanRequest.assigneeId)}
                            onChange={pickFilter('assigneeId')}
                            placeholder={t('SelectAssignee')}
                        />
                    </XtramField>
                    <XtramField label={t('Status')}>
                        <XtramSelect
                            direction={direction}
                            options={statusOptions}
                            value={selectedOption(statusOptions, workPlanRequest.status)}
                            onChange={pickFilter('status')}
                            placeholder={t('SelectStatus')}
                        />
                    </XtramField>
                </div>
            </div>

            <div className={styles.section}>
                <div className={styles.toolbar}>
                    <h3 className={styles.sectionTitle}>
                        {t('AssignmentSelection')}
                        {selectedAssignments.length > 0 && ` (${selectedAssignments.length} ${t('Selected')})`}
                    </h3>
                    <XtramCheckbox
                        checked={allSelected}
                        onChange={handleSelectAllAssignments}
                        label={t('SelectAll')}
                    />
                </div>
                <p className={styles.muted}>{t('WorkPlanPeriodNote')}</p>

                <XtramTable height="medium">
                    <Table size="small" stickyHeader dir={direction}>
                        <TableHead>
                            <TableRow>
                                <TableCell padding="checkbox" className={styles.actionsCell}>
                                    <Checkbox
                                        size="small"
                                        className={styles.tableCheckbox}
                                        checked={allSelected}
                                        indeterminate={someSelected}
                                        onChange={(e) => handleSelectAllAssignments(e.target.checked)}
                                    />
                                </TableCell>
                                <TableCell>{t('Summary')}</TableCell>
                                <TableCell>{t('Assignee')}</TableCell>
                                <TableCell>{t('Client')}</TableCell>
                                <TableCell>{t('Priority')}</TableCell>
                                <TableCell>{t('Status')}</TableCell>
                                <TableCell>{t('DueDate')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredAssignments.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} className={styles.emptyState}>
                                        {t('NoAssignmentsFound')}
                                    </TableCell>
                                </TableRow>
                            ) : filteredAssignments
                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                .map((assignment) => (
                                    <TableRow key={assignment.id} hover>
                                        <TableCell padding="checkbox" className={styles.actionsCell}>
                                            <Checkbox
                                                size="small"
                                                className={styles.tableCheckbox}
                                                checked={selectedAssignments.includes(assignment.id)}
                                                onChange={(e) => handleAssignmentSelection(assignment.id, e.target.checked)}
                                            />
                                        </TableCell>
                                        <TableCell className={styles.wrapCell}>{assignment.summary}</TableCell>
                                        <TableCell>
                                            <UserAvatar name={getAssigneeName(assignment.assigneeId)} size={18} />
                                        </TableCell>
                                        <TableCell>
                                            <UserAvatar name={getClientName(assignment.caseId)} size={18} />
                                        </TableCell>
                                        <TableCell>{renderPriority(assignment.priority)}</TableCell>
                                        <TableCell>{renderStatus(assignment.status)}</TableCell>
                                        <TableCell>
                                            {assignment.dueDate ? dayjs(assignment.dueDate).format('DD/MM/YYYY') : '-'}
                                        </TableCell>
                                    </TableRow>
                                ))}
                        </TableBody>
                    </Table>
                    <TablePagination
                        component="div"
                        count={filteredAssignments.length}
                        page={page}
                        onPageChange={(e, newPage) => setPage(newPage)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={(e) => {
                            setRowsPerPage(parseInt(e.target.value, 10));
                            setPage(0);
                        }}
                        labelRowsPerPage={t('RowsPerPage')}
                        labelDisplayedRows={({ from, to, count }) => `${from}-${to} ${t('of')} ${count}`}
                    />
                </XtramTable>
            </div>
        </>
    );

    const renderReport = () => {
        const { summary } = generatedReport;
        const completion = (summary.completionRate || 0) * 100;

        return (
            <>
                <div className={styles.section}>
                    <div className={styles.stats}>
                        <XtramStat label={t('TotalAssignments')} value={summary.totalAssignments} />
                        <XtramStat label={t('Completed')} value={summary.completedAssignments} tone="success" />
                        <XtramStat label={t('InProgress')} value={summary.inProgressAssignments} tone="warning" />
                        <XtramStat label={t('Pending')} value={summary.pendingAssignments} tone="error" />
                    </div>
                </div>

                <div className={styles.section}>
                    <div className={styles.toolbar}>
                        <h3 className={styles.sectionTitle}>{t('CompletionRate')}</h3>
                        <span className={styles.sectionTitle}>{completion.toFixed(1)}%</span>
                    </div>
                    <LinearProgress
                        variant="determinate"
                        value={completion}
                        sx={{
                            height: 12,
                            borderRadius: 999,
                            backgroundColor: '#e5e7f6',
                            '& .MuiLinearProgress-bar': { backgroundColor: 'var(--xtram-blue)', borderRadius: 999 },
                        }}
                    />
                </div>

                <div className={styles.section}>
                    <h3 className={styles.sectionTitle}>{t('Assignments')}</h3>
                    <XtramTable height="medium">
                        <Table size="small" stickyHeader dir={direction}>
                            <TableHead>
                                <TableRow>
                                    <TableCell>{t('Summary')}</TableCell>
                                    <TableCell>{t('Assignee')}</TableCell>
                                    <TableCell>{t('Client')}</TableCell>
                                    <TableCell>{t('Priority')}</TableCell>
                                    <TableCell>{t('Status')}</TableCell>
                                    <TableCell>{t('DueDate')}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {generatedReport.assignments?.length > 0 ? (
                                    generatedReport.assignments.map((assignment) => (
                                        <TableRow key={assignment.id} hover>
                                            <TableCell className={styles.wrapCell}>{assignment.summary}</TableCell>
                                            <TableCell>
                                                <UserAvatar name={assignment.assigneeName || t('NoAssignee')} size={18} />
                                            </TableCell>
                                            <TableCell>
                                                <UserAvatar name={assignment.clientName || t('Office')} size={18} />
                                            </TableCell>
                                            <TableCell>{renderPriority(assignment.priority)}</TableCell>
                                            <TableCell>{renderStatus(assignment.status)}</TableCell>
                                            <TableCell>
                                                {assignment.dueDate ? dayjs(assignment.dueDate).format('DD/MM/YYYY') : '-'}
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={6} className={styles.emptyState}>
                                            {t('NoAssignmentsFound')}
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </XtramTable>
                </div>

                {summary.assigneeSummaries?.length > 0 && (
                    <div className={styles.section}>
                        <h3 className={styles.sectionTitle}>{t('AssigneeSummaries')}</h3>
                        <XtramTable>
                            <Table size="small" dir={direction}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>{t('Assignee')}</TableCell>
                                        <TableCell className={styles.actionsCell}>{t('TotalAssigned')}</TableCell>
                                        <TableCell className={styles.actionsCell}>{t('Completed')}</TableCell>
                                        <TableCell className={styles.actionsCell}>{t('InProgress')}</TableCell>
                                        <TableCell className={styles.actionsCell}>{t('Pending')}</TableCell>
                                        <TableCell className={styles.actionsCell}>{t('CompletionRate')}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {summary.assigneeSummaries.map((assignee, index) => (
                                        <TableRow key={index} hover>
                                            <TableCell>
                                                <UserAvatar name={assignee.assigneeName} size={18} />
                                            </TableCell>
                                            <TableCell className={styles.actionsCell}>{assignee.totalAssigned}</TableCell>
                                            <TableCell className={`${styles.actionsCell} ${styles.textSuccess}`}>{assignee.completed}</TableCell>
                                            <TableCell className={`${styles.actionsCell} ${styles.textWarning}`}>{assignee.inProgress}</TableCell>
                                            <TableCell className={`${styles.actionsCell} ${styles.textError}`}>{assignee.pending}</TableCell>
                                            <TableCell className={styles.actionsCell}>
                                                <XtramBadge
                                                    tone={assignee.completionRate >= 0.8 ? 'success' : assignee.completionRate >= 0.5 ? 'warning' : 'error'}
                                                >
                                                    {`${(assignee.completionRate * 100).toFixed(1)}%`}
                                                </XtramBadge>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </XtramTable>
                    </div>
                )}
            </>
        );
    };

    return (
        <XtramModal
            open={open}
            onClose={onClose}
            direction={direction}
            expanded
            busy={loading}
            title={t('WorkPlanManager')}
            footer={!showReport ? (
                <>
                    <XtramButton variant="secondary" onClick={onClose} disabled={loading}>
                        {t('Cancel')}
                    </XtramButton>
                    <XtramButton variant="primary" onClick={handleGenerateReport} disabled={loading} busy={loading}>
                        <ViewIcon fontSize="small" />
                        {t('GenerateReport')}
                    </XtramButton>
                </>
            ) : (
                <>
                    <XtramButton variant="secondary" onClick={() => setShowReport(false)} disabled={loading}>
                        {t('Back')}
                    </XtramButton>
                    <XtramButton variant="ghost" onClick={handleDownloadPdf} disabled={loading}>
                        <PdfIcon fontSize="small" />
                        {t('DownloadPdf')}
                    </XtramButton>
                    <XtramButton variant="primary" onClick={handleDownloadExcel} disabled={loading} busy={loading}>
                        <ExcelIcon fontSize="small" />
                        {t('DownloadExcel')}
                    </XtramButton>
                </>
            )}
        >
            {showReport && generatedReport ? renderReport() : renderConfiguration()}
        </XtramModal>
    );
};

export default WorkPlanManager;
